import React, { useState } from 'react';
import { Search, GripVertical, Type, Hash, Calendar, CheckSquare, Mail, Phone, Link as LinkIcon, DollarSign, Percent, FileText } from 'lucide-react';
import type { CustomField } from '../../../services/queries/fieldQueries';

interface FieldPaletteProps {
  availableFields: CustomField[];
  onDragStart: (field: CustomField) => void;
  onDragEnd: () => void;
}

export const FieldPalette: React.FC<FieldPaletteProps> = ({
  availableFields,
  onDragStart,
  onDragEnd
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFields = availableFields.filter(field =>
    field.fieldLabel.toLowerCase().includes(searchQuery.toLowerCase()) ||
    field.fieldName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFieldIcon = (type: string) => {
    const iconMap: Record<string, any> = {
      text: Type,
      number: Hash,
      date: Calendar,
      datetime: Calendar,
      checkbox: CheckSquare,
      email: Mail,
      phone: Phone,
      url: LinkIcon,
      currency: DollarSign,
      percent: Percent,
      textarea: FileText,
      longtextarea: FileText,
      richtextarea: FileText
    };
    const Icon = iconMap[type] || Type;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Available Fields ({filteredFields.length})
        </h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Field List */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="space-y-2">
          {filteredFields.map(field => (
            <div
              key={field.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/field', JSON.stringify({
                  id: field.id,
                  fieldName: field.fieldName,
                  fieldLabel: field.fieldLabel,
                  fieldType: field.fieldType,
                  required: field.required,
                }));
                e.dataTransfer.effectAllowed = 'copy';
                onDragStart(field);
              }}
              onDragEnd={onDragEnd}
              className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-move hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-purple-300 dark:hover:border-purple-600 transition-colors group"
            >
              <GripVertical className="w-4 h-4 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
              <div className="p-1.5 bg-white dark:bg-gray-800 rounded">
                {getFieldIcon(field.fieldType)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {field.fieldLabel}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {field.fieldName}
                </div>
              </div>
              {field.required && (
                <span className="text-xs text-red-600 dark:text-red-400 font-medium">*</span>
              )}
            </div>
          ))}
        </div>

        {filteredFields.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            No fields found
          </div>
        )}
      </div>
    </div>
  );
};
